import Vue from "vue";
import Router from "vue-router";
import axios from "../plugins/axios";
import store from "../store/index";
import errorHandler from "../plugins/stackdriver";

Vue.use(Router);

let home = {
  path: "/",
  component: () => import("@/views/Layout/NonAuthLayout.vue"),
  name: "Authentication",
  redirect: "/home",
  children: [
    {
      path: "/home",
      name: "Home",
      component: () =>
        import(/* webpackChunkName: "home" */ "@/views/Pages/Home.vue"),
      meta: {
        noBodyBackground: true
      }
    },
    {
      path: "/login",
      name: "Login",
      meta: {
        requiresGuest: true
      },
      component: () =>
        import(/* webpackChunkName: "login" */ "@/views/Pages/Login.vue")
    },
    {
      path: "/register",
      name: "Register",
      meta: {
        requiresGuest: true
      },
      component: () =>
        import(/* webpackChunkName: "register" */ "@/views/Pages/Register.vue")
    },
    {
      path: "/pricing",
      name: "Pricing",
      component: () =>
        import(/* webpackChunkName: "pricing" */ "@/views/Pages/Pricing.vue")
    },
    {
      path: "/terms",
      name: "Terms",
      component: () =>
        import(/* webpackChunkName: "legal" */ "@/views/Pages/Terms.vue")
    },
    {
      path: "/privacy",
      name: "Privacy",
      component: () =>
        import(/* webpackChunkName: "legal" */ "@/views/Pages/Privacy.vue")
    },
    {
      path: "/payment",
      name: "Payment",
      component: () =>
        import(/* webpackChunkName: "payment" */ "@/views/Pages/Payment.vue")
    },
    {
      path: "/about",
      name: "About",
      component: () =>
        import(/* webpackChunkName: "about" */ "@/views/Pages/About.vue")
    },
    {
      path: "/handleSlackWebhook",
      name: "HandleSlackWebhook",
      component: () =>
        import(
          /* webpackChunkName: "slack" */ "@/views/Pages/HandleSlackWebhook.vue"
        )
    },
    {
      path: "features",
      name: "features",
      component: () =>
        import(
          /* webpackChunkName: "features" */ "@/views/Layout/FeaturesLayout.vue"
        ),
      children: [
        {
          path: "/why-boltscale",
          name: "why-boltscale",
          component: () =>
            import(
              /* webpackChunkName: "features" */ "@/views/Pages/WhyBoltscale.vue"
            ),
          meta: {
            noBodyBackground: true
          }
        },
        {
          path: "public-status-pages",
          name: "public-status-pages",
          component: () =>
            import(
              /* webpackChunkName: "features" */ "@/views/Pages/Features/PublicStatusPages.vue"
            ),
          meta: {
            noBodyBackground: true
          }
        },
        {
          path: "private-status-pages",
          name: "private-status-pages",
          component: () =>
            import(
              /* webpackChunkName: "features" */ "@/views/Pages/Features/PrivateStatusPages.vue"
            ),
          meta: {
            noBodyBackground: true
          }
        }
      ]
    }
  ]
};

let app = {
  path: "/app",
  name: "dashboardLayout",
  component: () =>
    import(/* webpackChunkName: "monitor" */ "@/views/Layout/AuthLayout.vue"),
  children: [
    {
      name: "dashboard",
      path: "dashboard",
      component: () =>
        import(
          /* webpackChunkName: "monitor" */ "@/views/Custom/LoggedInStatusPage.vue"
        ),
      meta: {
        requiresAuth: true,
        requiresSetupCompleted: true,
        fullWidthLayout: true,
        darkHeader: true
      }
    },
    {
      path: "setup",
      name: "setup",
      component: () =>
        import(/* webpackChunkName: "setup" */ "@/views/Custom/Setup.vue"),
      meta: {
        requiresAuth: true,
        requiresSetupNotCompleted: true,
        fullWidthLayout: true,
        noBodyBackground: true
      }
    },
    {
      path: "settings",
      name: "settings",
      redirect: "/app/settings/notifications",
      component: () =>
        import(
          /* webpackChunkName: "account" */ "@/views/Layout/SettingsLayout.vue"
        ),
      children: [
        {
          path: "account",
          name: "account_settings",
          component: () =>
            import(
              /* webpackChunkName: "account" */ "@/views/Pages/Settings/Account.vue"
            ),
          meta: {
            requiresAuth: true,
            fullWidthLayout: true,
            readableName: "Account",
            noBodyBackground: true
          }
        },
        {
          path: "notifications",
          name: "notification_settings",
          component: () =>
            import(
              /* webpackChunkName: "account" */ "@/views/Pages/Settings/Notifications.vue"
            ),
          meta: {
            requiresAuth: true,
            fullWidthLayout: true,
            readableName: "Notifications",
            noBodyBackground: true
          }
        },
        {
          path: "statuspage",
          name: "status_page_settings",
          component: () =>
            import(
              /* webpackChunkName: "account" */ "@/views/Pages/Settings/StatusPage.vue"
            ),
          meta: {
            requiresAuth: true,
            fullWidthLayout: true,
            readableName: "Status Page",
            noBodyBackground: true
          }
        }
      ]
    }
  ]
};

let publicStatusPage = {
  path: "/status",
  name: "statusLayout",
  component: () =>
    import(
      /* webpackChunkName: "monitor" */ "@/views/Layout/PublicStatusPageLayout.vue"
    ),
  children: [
    {
      path: ":name",
      name: "monitor",
      component: () =>
        import(
          /* webpackChunkName: "monitor" */ "@/views/Custom/PublicStatusPage.vue"
        ),
      meta: {
        fullWidthLayout: true
      }
    }
  ]
};

const router = new Router({
  mode: "history",

  routes: [
    home,
    app,
    publicStatusPage,
    {
      path: "*",
      name: "notfound",

      component: () =>
        import(
          /* webpackChunkName: "notfound" */ "@/views/GeneralViews/NotFoundPage.vue"
        )
    }
  ],
  scrollBehavior: (to, from, savedPosition) => {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return { selector: to.hash };
    }
    return { x: 0, y: 0 };
  }
});

router.beforeEach(async (to, from, next) => {
  let loggedIn = false;
  let user = {};
  if (localStorage.user) {
    user = JSON.parse(localStorage.user);
    store.commit("user/updateUser", user);
    loggedIn = true;
    Vue.$gtag.set({
      user_id: user.id.toString()
    });

    errorHandler.setUser(user.email);
    window.heap.identify(user.email);

    const properties = {};
    if (user.websites.length > 0) {
      properties.has_website = true;
      if (user.websites[0].components) {
        properties.num_components = user.websites[0].components.length;
      }

      if (user.websites[0].name) {
        properties.website_name = user.websites[0].name;
      }
    }

    window.heap.addUserProperties(properties);
  }

  let userHasWebsite = false;
  if (loggedIn && user.websites && user.websites.length > 0) {
    userHasWebsite = true;
  }

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    console.log("Next route requires auth");
    if (!loggedIn) {
      console.log("Not logged in so redirecting to login");
      return next({
        path: "/login",
        params: { nextUrl: to.fullPath }
      });
    }
  } else if (to.matched.some((record) => record.meta.requiresGuest)) {
    console.log("Next route requires guest");
    if (userHasWebsite) {
      console.log("Logged in so redirecting to dashboard");
      return next({
        path: "/app/dashboard"
      });
    } else if (loggedIn) {
      console.log("Going to setup!");
      return next({
        path: "/app/setup"
      });
    }
  }

  if (to.matched.some((record) => record.meta.requiresSetupNotCompleted)) {
    if (userHasWebsite) {
      return next({
        path: "/app/dashboard"
      });
    }
  }

  if (to.matched.some((record) => record.meta.requiresSetupCompleted)) {
    if (!userHasWebsite) {
      return next({
        path: "/app/setup"
      });
    }
  }

  next();
});

router.afterEach(async ({ path, name }) => {
  Vue.analytics.fbq.event("PageView");
  store.dispatch("user/auth");
});

export default router;
